import * as HelpersModule from './helpers.js';

/**
 * Review the bytes before they land.
 *
 * This is the one surface in the product that promises an operator sees what is
 * about to be written — the policy bundle, the hook service, the signed evidence
 * package, a codegen bundle from an agent. Four callers, one look.
 *
 * It draws in a CONTENT TAB, and falls back to a dialog only where there is no
 * content area to draw in. A modal was the wrong container: the decision here is
 * not a moment's yes/no but a read, and reading twelve files needs the tab
 * strip, the editor's full height and the freedom to leave it open and look at
 * something else. Measured in IVYX Studio on 2026-08-28: inside the dialog the
 * content pane sat at a hardcoded 400px in a 700px viewport and still cut its
 * text off mid-word, while `width: 90vw` left most of a wide display empty.
 *
 * What the move costs, stated rather than discovered: the preview no longer
 * blocks. The promise can stay pending while the operator does something else,
 * and a second preview under the same title supersedes the first rather than
 * queueing behind it. Neither is a regression for the four callers — each
 * computes its artefacts up front and confirms THOSE bytes — but "the dialog
 * blocked" was an implicit guarantee and is now gone.
 */

const Helpers = HelpersModule.Helpers;

export interface ChangesPreviewFile {
  path: string;
  action: 'create' | 'update' | 'delete';
  content?: string;
}

export interface ChangesPreviewOptions {
  title: string;
  summary?: string;
  files: ChangesPreviewFile[];
  onConfirm: () => Promise<void>;
  onCancel?: () => void;
}

type Decision = 'confirm' | 'cancel' | undefined;

/**
 * Not `code`. `extension-code-editor` binds `mod+s` to `editor.saveActiveFile`,
 * which has no read-only guard and writes to whatever path the active tab
 * carries — so a tab claiming that view type would let one Cmd+S write a
 * `@changes/…` file into the workspace.
 */
const VIEW_TYPE = 'changes-preview';

const BORDER = '1px solid var(--divider, rgba(255, 255, 255, 0.1))';

/**
 * Per-key teardown for a preview that is still on screen, so reopening the same
 * one replaces it instead of rendering twice into the container `ContentTabs`
 * hands back unchanged.
 */
const openPreviews = new Map<string, () => void>();

/**
 * The tab's identity.
 *
 * `@`-prefixed synthetic paths are the fleet's convention for a tab that is not
 * a file (`extension-source-control` opens a commit as `@6051e01`). The title is
 * carried through as written rather than slugged: this string is what the tab's
 * hover text shows, and slugging turns a Turkish title into consonant soup for
 * no gain. Only `/` is replaced, because that is the separator the tab registry
 * uses to decide what lives under a renamed directory.
 */
function previewKey(title: string): string {
  return `@changes/${(title || 'Review changes').replace(/\//g, ' ')}`;
}

/** Language id for Monaco, from the file extension. */
function getLanguageForPath(filePath: string): string {
  const ext = filePath.split('.').pop()?.toLowerCase();
  const langMap: Record<string, string> = {
    ts: 'typescript',
    tsx: 'typescript',
    js: 'javascript',
    jsx: 'javascript',
    py: 'python',
    json: 'json',
    md: 'markdown',
    html: 'html',
    css: 'css',
    yaml: 'yaml',
    yml: 'yaml'
  };
  return langMap[ext || ''] || 'plaintext';
}

interface PreviewBody {
  root: HTMLElement;
  /** Render the content pane. Call once the root is in the document. */
  mount: () => void;
  dispose: () => void;
}

/**
 * The two-column reading surface: a file list beside the selected file's bytes.
 *
 * Built as DOM rather than as an interpolated template, which is also what
 * retires the local escaper this file used to need — nothing here is parsed as
 * markup, so a path out of the workspace cannot introduce an element.
 *
 * The list is filled synchronously; the content pane waits for `mount()`,
 * because Monaco measures the element it is given and a detached node measures
 * zero.
 */
function buildPreviewBody(files: ChangesPreviewFile[]): PreviewBody {
  if (files.length === 0) {
    return {
      root: Helpers.createEmptyState({
        title: 'Nothing to review',
        detail: 'This change writes no files.',
        variant: 'section'
      }),
      mount: () => {},
      dispose: () => {}
    };
  }

  let selectedIndex = 0;
  let editor: { dispose: () => void } | null = null;

  const listBox = Helpers.createBox({
    style: { flex: '1', minHeight: '0', overflowY: 'auto', padding: '4px' }
  });

  const pathLabel = Helpers.createTypography(files[0]?.path ?? '', {
    fontWeight: 'bold',
    attrs: { truncate: '1' },
    style: { minWidth: '0' }
  });

  const contentBox = Helpers.createBox({
    style: {
      flex: '1',
      minHeight: '0',
      minWidth: '0',
      display: 'flex',
      overflow: 'auto'
    }
  });

  const renderFileList = (): void => {
    listBox.innerHTML = '';
    files.forEach((file, index) => {
      const badge = Helpers.createBadge({
        text: file.action,
        variant: 'filled',
        color:
          file.action === 'delete'
            ? 'error'
            : file.action === 'create'
              ? 'success'
              : 'primary',
        size: 'small'
      });
      // The filename is the label and the directory is the secondary line, not
      // the whole path in one truncated row. Measured while reviewing a twelve
      // file write in a 300px column: `.punica/policy-export/hook/src/__tests__/`
      // filled the row, so `README.md`, `decide.test.mjs` and `server.test.mjs`
      // rendered as three identical `…/src/__test…` lines — an unreadable list in
      // the one surface whose job is to be read before anything is written.
      //
      // `content` was `file.action`, which the badge already says, so this costs
      // nothing and removes a duplication.
      const slash = file.path.lastIndexOf('/');
      const item = Helpers.createListViewItem({
        label: slash >= 0 ? file.path.slice(slash + 1) : file.path,
        content: slash > 0 ? file.path.slice(0, slash) : '',
        endAdornment: badge
      });
      if (index === selectedIndex) {
        item.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
      }
      item.style.cursor = 'pointer';
      item.addEventListener('click', () => {
        if (index === selectedIndex) return;
        selectedIndex = index;
        renderFileList();
        renderFileContent();
      });
      listBox.appendChild(item);
    });
  };

  const renderPreFallback = (host: HTMLElement, content: string): void => {
    const pre = document.createElement('pre');
    pre.style.cssText =
      'margin: 0; padding: 10px; overflow: auto; flex: 1; min-width: 0; font-family: monospace; font-size: 12px; white-space: pre-wrap; word-wrap: break-word;';
    pre.textContent = content;
    host.innerHTML = '';
    host.appendChild(pre);
  };

  const renderFileContent = (): void => {
    const file = files[selectedIndex];
    pathLabel.textContent = file?.path ?? '';

    if (editor) {
      try {
        editor.dispose();
      } catch {
        // A disposed editor is the state we wanted anyway.
      }
      editor = null;
    }
    contentBox.innerHTML = '';
    if (!file) return;

    if (file.action === 'delete') {
      contentBox.appendChild(
        Helpers.createTypography(`This file will be deleted: ${file.path}`, {
          variant: 'body1',
          style: { padding: '10px' }
        })
      );
      return;
    }

    if (!file.content) {
      contentBox.appendChild(
        Helpers.createTypography('No content preview available', {
          variant: 'body1',
          style: { padding: '10px' }
        })
      );
      return;
    }

    // The editor FILLS its column rather than sitting at a fixed height. That
    // is the whole reason this surface moved out of a dialog.
    const editorContainer = Helpers.createBox({
      style: {
        flex: '1',
        minWidth: '0',
        minHeight: '0',
        width: '100%',
        overflow: 'hidden'
      }
    });
    contentBox.appendChild(editorContainer);

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const monacoGlobal = (window as any).monaco || (globalThis as any).monaco;
    if (monacoGlobal?.editor) {
      try {
        editor = monacoGlobal.editor.create(editorContainer, {
          value: file.content || '',
          language: getLanguageForPath(file.path),
          theme: 'vs-dark',
          readOnly: true,
          minimap: { enabled: false },
          fontSize: 13,
          fontLigatures: true,
          automaticLayout: true,
          scrollBeyondLastLine: false
        });
        return;
      } catch {
        // Monaco is present but would not build one here; the text still has
        // to be readable, which is what this surface is for.
      }
    }
    renderPreFallback(editorContainer, file.content || '');
  };

  const header = (child: HTMLElement): HTMLElement =>
    Helpers.createBox({
      style: {
        flex: '0 0 auto',
        padding: '8px',
        minWidth: '0',
        borderBottom: BORDER
      },
      children: [child]
    });

  const left = Helpers.createBox({
    style: {
      // 300px where there is room, and never more than a third of it.
      //
      // A fixed lane is not fixed by default here — `flex-shrink` is 1 unless
      // it is said otherwise, and this column has drifted before. But a flat
      // 300 was sized for a 90vw dialog: the content area is whatever the
      // sidebar leaves, so at 500px the list would take 60% of it and hand the
      // bytes a 188px column — the same unreadable pane this surface moved out
      // of a dialog to escape. `min-width` beats `max-width` in CSS, so the
      // floor holds and a filename stays legible.
      flex: '0 0 300px',
      flexShrink: '0',
      maxWidth: '30%',
      minWidth: '180px',
      display: 'flex',
      flexDirection: 'column',
      border: BORDER,
      borderRadius: '4px',
      overflow: 'hidden'
    },
    children: [
      header(
        Helpers.createTypography(`Files (${files.length})`, {
          fontWeight: 'bold'
        })
      ),
      listBox
    ]
  });

  const right = Helpers.createBox({
    style: {
      flex: '1',
      minWidth: '0',
      display: 'flex',
      flexDirection: 'column',
      border: BORDER,
      borderRadius: '4px',
      overflow: 'hidden'
    },
    children: [header(pathLabel), contentBox]
  });

  const root = Helpers.createBox({
    style: {
      display: 'flex',
      gap: '12px',
      flex: '1',
      minHeight: '0'
    },
    children: [left, right]
  });

  renderFileList();

  return {
    root,
    mount: renderFileContent,
    dispose: () => {
      if (!editor) return;
      try {
        editor.dispose();
      } catch {
        // ignore
      }
      editor = null;
    }
  };
}

/** Unwrap a kernel event to its payload; `publish` and `publishEvent` differ. */
function eventPath(raw: unknown): string | undefined {
  const obj = raw as Record<string, unknown> | null | undefined;
  if (!obj || typeof obj !== 'object') return undefined;
  const payload =
    typeof obj.name === 'string' && obj.payload
      ? (obj.payload as Record<string, unknown>)
      : obj;
  const path = payload.path;
  return typeof path === 'string' && path ? path : undefined;
}

function subscribeToClose(
  listener: (path: string) => void
): (() => void) | null {
  try {
    const handle = punica.kernel.Events.manager.subscribe(
      'shell.contentTabs.didClose',
      (raw: unknown) => {
        const path = eventPath(raw);
        if (path) listener(path);
      }
    );
    return typeof handle === 'function' ? handle : null;
  } catch {
    // No event bus (headless, tests). The buttons still decide; only the
    // close-the-tab route stops reporting itself.
    return null;
  }
}

async function reportConfirmFailure(error: unknown): Promise<void> {
  try {
    await punica.shell.View.showAlert({
      title: 'Error',
      message: `Failed to apply changes: ${error instanceof Error ? error.message : String(error)}`,
      variant: 'error'
    });
  } catch {
    // A host without an alert surface still gets the `undefined` result.
  }
}

/** The default: draw into the content tab `ContentTabs` handed back. */
function showInTab(
  container: HTMLElement,
  key: string,
  options: ChangesPreviewOptions
): Promise<Decision> {
  // Whatever was under this key is not the review being asked for now.
  openPreviews.get(key)?.();

  return new Promise<Decision>((resolve) => {
    let decided = false;
    /**
     * `disabled` on an already-built button does not remove the listener
     * `createButton` attached, so a second click during an in-flight
     * `onConfirm` would run it again.
     */
    let applying = false;
    let unsubscribe: (() => void) | null = null;
    const body = buildPreviewBody(options.files);

    const teardown = (): void => {
      unsubscribe?.();
      unsubscribe = null;
      if (openPreviews.get(key) === supersede) openPreviews.delete(key);
      body.dispose();
    };

    /**
     * `close` asks about unsaved changes; a preview never sets `dirty`, so that
     * prompt could only ever be a question with no answer.
     */
    const closeTab = (): void => {
      try {
        punica.shell.ContentTabs.forceClose(key);
      } catch {
        // The tab is already gone, which is the state we wanted.
      }
    };

    const finish = (decision: Decision, close: boolean): void => {
      if (decided) return;
      decided = true;
      // Before `forceClose`, or its own `didClose` re-enters here.
      teardown();
      if (close) closeTab();
      resolve(decision);
    };

    /**
     * Replaced, not cancelled — so `undefined`, which the result type already
     * carries for "no decision was made", and no `onCancel`.
     */
    const supersede = (): void => {
      if (decided) return;
      decided = true;
      teardown();
      resolve(undefined);
    };
    openPreviews.set(key, supersede);

    unsubscribe = subscribeToClose((path) => {
      if (path !== key) return;
      options.onCancel?.();
      // Already gone; closing it again would be a second teardown.
      finish('cancel', false);
    });

    // The tab label truncates at 150px, so the title is drawn here too —
    // "Evidence for run a94fb70d…5ecc" does not fit on a tab.
    const heading = Helpers.createTypography(options.title, {
      variant: 'subtitle1',
      fontWeight: 'bold'
    });

    const headerChildren = [heading];
    if (options.summary) {
      headerChildren.push(
        Helpers.createTypography(options.summary, {
          variant: 'body2',
          style: { color: 'var(--text-secondary)' }
        })
      );
    }

    const cancelButton = Helpers.createButton(
      'Cancel',
      () => {
        options.onCancel?.();
        finish('cancel', true);
      },
      { variant: 'outlined', size: 'small' }
    );

    const applyButton = Helpers.createButton(
      'Apply Changes',
      () => {
        if (decided || applying) return;
        applying = true;
        applyButton.setAttribute('disabled', '');
        cancelButton.setAttribute('disabled', '');
        void options
          .onConfirm()
          .then(() => finish('confirm', true))
          .catch(async (error) => {
            await reportConfirmFailure(error);
            finish(undefined, true);
          });
      },
      // `filled` with no `color` renders transparent, so the one button that
      // writes to disk would read as plain text.
      { variant: 'filled', color: 'primary', size: 'small' }
    );

    const footer = Helpers.createBox({
      style: {
        flex: '0 0 auto',
        display: 'flex',
        gap: '8px',
        justifyContent: 'flex-end',
        paddingTop: '12px',
        borderTop: BORDER
      },
      children: [cancelButton, applyButton]
    });

    const root = Helpers.createBox({
      style: {
        height: '100%',
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
        padding: '12px',
        minHeight: '0'
      },
      children: [
        Helpers.createBox({
          style: {
            flex: '0 0 auto',
            display: 'flex',
            flexDirection: 'column',
            gap: '4px'
          },
          children: headerChildren
        }),
        body.root,
        footer
      ]
    });

    container.innerHTML = '';
    container.appendChild(root);
    body.mount();
  });
}

/**
 * The fallback: a host whose shell has no content area to open a tab in.
 *
 * Kept rather than deleted — `ContentTabs.open` answers `null` when the layout
 * carries no `content` slot, and a confirmation that cannot be asked for is
 * worse than one asked for in a dialog.
 */
function showInDialog(options: ChangesPreviewOptions): Promise<Decision> {
  const { title, summary, files, onConfirm, onCancel } = options;
  const bodyId = `changes-preview-body-${Date.now()}`;
  const body = buildPreviewBody(files);

  const html = `
    <punica-box id="${bodyId}" style="display: flex; flex-direction: column; gap: 12px; min-width: 800px; max-width: 1200px; height: 60vh; max-height: 80vh;"></punica-box>
  `;

  return new Promise((resolve) => {
    void punica.shell.View.showDialog({
      title,
      html,
      buttons: [
        { label: 'Cancel', variant: 'outlined', action: 'cancel' },
        {
          label: 'Apply Changes',
          variant: 'filled',
          color: 'primary',
          action: 'confirm'
        }
      ],
      width: '90vw',
      onMount: (dialog: HTMLElement) => {
        const host = dialog.querySelector(`#${bodyId}`) as HTMLElement | null;
        if (!host) return;
        if (summary) {
          host.appendChild(
            Helpers.createTypography(summary, { variant: 'body2' })
          );
        }
        host.appendChild(body.root);
        body.mount();
      }
    }).then(async (action) => {
      body.dispose();

      if (action === 'confirm') {
        try {
          await onConfirm();
          resolve('confirm');
        } catch (error) {
          await reportConfirmFailure(error);
          resolve(undefined);
        }
      } else {
        onCancel?.();
        resolve(action === 'cancel' ? 'cancel' : undefined);
      }
    });
  });
}

export async function showChangesPreview(
  options: ChangesPreviewOptions
): Promise<Decision> {
  const key = previewKey(options.title);

  let container: HTMLElement | null = null;
  try {
    container =
      punica.shell?.ContentTabs?.open?.({
        path: key,
        title: options.title,
        viewType: VIEW_TYPE
      }) ?? null;
  } catch {
    container = null;
  }

  return container ? showInTab(container, key, options) : showInDialog(options);
}
